import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5025';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// --- Proactive token refresh ---
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

function getTokenExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function scheduleTokenRefresh() {
  if (refreshTimer) clearTimeout(refreshTimer);

  const token = localStorage.getItem('admin-accessToken');
  if (!token) return;

  const expiry = getTokenExpiry(token);
  if (!expiry) return;

  // Refresh 5 minutes before expiry
  const delay = expiry - Date.now() - 5 * 60 * 1000;
  if (delay <= 0) {
    doProactiveRefresh();
    return;
  }

  refreshTimer = setTimeout(doProactiveRefresh, delay);
}

async function doProactiveRefresh() {
  const refreshToken = localStorage.getItem('admin-refreshToken');
  if (!refreshToken) return;

  try {
    const { data } = await axios.post(`${API_BASE_URL}/api/v1/auth/refresh`, { refreshToken });
    localStorage.setItem('admin-accessToken', data.accessToken);
    localStorage.setItem('admin-refreshToken', data.refreshToken);
    scheduleTokenRefresh();
  } catch {
    // Refresh failed — will fallback to 401 interceptor on next API call
  }
}

scheduleTokenRefresh();

globalThis.addEventListener('storage', (e) => {
  if (e.key === 'admin-accessToken') scheduleTokenRefresh();
});

export { scheduleTokenRefresh };

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin-accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('admin-refreshToken');

      if (refreshToken) {
        try {
          const { data } = await axios.post(`${API_BASE_URL}/api/v1/auth/refresh`, {
            refreshToken,
          });
          localStorage.setItem('admin-accessToken', data.accessToken);
          localStorage.setItem('admin-refreshToken', data.refreshToken);
          originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
          return api(originalRequest);
        } catch {
          localStorage.removeItem('admin-accessToken');
          localStorage.removeItem('admin-refreshToken');
          window.location.href = '/login';
        }
      } else {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export default api;
